import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { customerService } from '../../api/apiClient';

export default function EditUserDialog({ open, user, onClose, onSave }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [loadCustomersState, setLoadCustomersState] = useState({ loading: false, options: [], error: null });

  const [form, setForm] = useState({
    contactEmail: '',
    displayName: '',
    customerId: '',
    isActive: true
  });

  const canSubmit = useMemo(() => {
    if (!user?.userId) return false;
    if (!String(form.customerId || '').trim()) return false;
    return true;
  }, [form.customerId, user?.userId]);

  // Initialize/reset form when dialog opens or user changes.
  useEffect(() => {
    if (!open) return;
    setIsSubmitting(false);
    setFormError('');
    setForm({
      contactEmail: user?.contactEmail || '',
      displayName: user?.displayName || '',
      customerId: user?.customerId || '',
      isActive: !!user?.isActive
    });
  }, [open, user]);

  // Load customers for selector (with fallback to free-text if it fails)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function load() {
      setLoadCustomersState({ loading: true, options: [], error: null });
      const resp = await customerService.getCustomers(500, 0);
      if (cancelled) return;
      if (!resp.success) {
        setLoadCustomersState({ loading: false, options: [], error: resp.message || 'Failed to load customers' });
        return;
      }
      const customers = resp.data?.data || resp.data || [];
      const ids = (Array.isArray(customers) ? customers : [])
        .map((c) => c.customerId)
        .filter(Boolean);
      setLoadCustomersState({ loading: false, options: ids, error: null });
    }

    load();
    return () => { cancelled = true; };
  }, [open]);

  const handleClose = () => {
    if (isSubmitting) return;
    setFormError('');
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!user?.userId) return;
    setFormError('');
    setIsSubmitting(true);

    try {
      const payload = {
        contactEmail: String(form.contactEmail || '').trim() ? String(form.contactEmail).trim().toLowerCase() : null,
        displayName: String(form.displayName || '').trim() ? String(form.displayName).trim() : null,
        customerId: String(form.customerId || '').trim() ? String(form.customerId).trim() : null,
        isActive: !!form.isActive
      };

      const result = await onSave?.(user.userId, payload);
      if (result?.ok === false) {
        setFormError(result.message || 'Failed to update user');
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(false);
      onClose?.();
    } catch (e) {
      setIsSubmitting(false);
      setFormError(e?.message || 'Failed to update user');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="edit-user-dialog-title">
      <DialogTitle id="edit-user-dialog-title">Edit User</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}

          <Box>
            <Typography variant="caption" color="text.secondary">Login Email</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {user?.sabalLoginEmail || '—'}
            </Typography>
          </Box>

          <TextField
            label="Contact Email"
            value={form.contactEmail}
            onChange={(e) => setForm((p) => ({ ...p, contactEmail: e.target.value }))}
            disabled={isSubmitting}
            helperText="Optional (any domain)"
            autoComplete="off"
          />

          <TextField
            label="Display Name"
            value={form.displayName}
            onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
            disabled={isSubmitting}
            helperText="Optional"
          />

          <Autocomplete
            freeSolo
            options={loadCustomersState.options}
            loading={loadCustomersState.loading}
            value={form.customerId || ''}
            onInputChange={(_, value) => setForm((p) => ({ ...p, customerId: value }))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer ID"
                required
                disabled={isSubmitting}
                helperText={
                  loadCustomersState.error
                    ? `Could not load the customer list (${loadCustomersState.error}). You can still type a customerId.`
                    : 'Required for every user'
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadCustomersState.loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />

          <FormControlLabel
            control={
              <Switch
                checked={!!form.isActive}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
            }
            label={form.isActive ? 'Active' : 'Inactive'}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting || !canSubmit} variant="contained">
          {isSubmitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


